import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import FormInputText from '../../../common/Forms/FormTextField';
import { useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import moment from 'moment';
import {
  DialogHeader,
  DialogHeading,
  ButtonWrapper,
  CancelButton,
  SaveButton,
} from '../../../Episode/Episode.Styled';
import FormInputDropdown from '../../../common/Forms/FormDropdown';
import {
  CBP_CASE_MANAGER_UPDATE_URL,
  fieldRequired,
  formDateFormat,
} from '../../../../constants';
import {
  setCaseManager,
  setEndDate,
  setReferralType,
  setStartDate,
} from '../../../../redux/cbp/cbpCaseManager/cbpCaseManagerSlice';
import { postStopApi, setStopApi } from '../../../../services/StopApi/StopApi';
import FormSuccess from '../../../common/Forms/FormSuccess';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

const schema = yup.object().shape({
  startDate: yup.date().typeError(fieldRequired).required(),
  endDate: yup.date().typeError(fieldRequired).required(),
  caseManager: yup.string().required(fieldRequired),
  referralType: yup.string().required(fieldRequired),
});

const CBPCaseManagerDialog = (props) => {
  const { open, setOpen, openBackdrop, cbpId, authToken, fetchData } = props;

  const dispatch = useDispatch();
  const [formSuccess, setFormSuccess] = useState(false);
  const [isNewCaseManger, setIsNewCaseManager] = useState(false);

  const cbpCaseManagerForm = useSelector((state) => state?.cbpCaseManagerForm);
  const cbpCaseManagerReferenceData = useSelector(
    (state) => state.cbpCaseManagerReferenceData
  );

  const defaultValue = {
    caseManager: cbpCaseManagerForm?.caseManager?.value
      ? cbpCaseManagerForm?.caseManager?.value
      : '',
    referralType: cbpCaseManagerForm?.referralType?.athTypId
      ? cbpCaseManagerForm?.referralType?.athTypId
      : '',
    startDate: moment(cbpCaseManagerForm?.startDate)
      .format(formDateFormat)
      .split('T')[0],
    endDate: moment(cbpCaseManagerForm?.endDate)
      .format(formDateFormat)
      .split('T')[0],
    CbpCmID: cbpCaseManagerForm?.CbpCmID,
  };
  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(schema),
  });
  const { handleSubmit, reset, control, register, formState } = useFunction;
  const { isValid, dirtyFields, errors } = formState;

  useEffect(() => {
    reset(defaultValue);
  }, [
    defaultValue.caseManager,
    defaultValue.referralType,
    defaultValue.startDate,
    defaultValue.endDate,
    defaultValue.CbpCmID,
  ]);

  const caseManager = useWatch({
    control,
    name: 'caseManager',
  });
  const caseManagerId = cbpCaseManagerReferenceData?.caseManagers?.filter(
    (item) => (item.value === caseManager ? item.id : '')
  );
  const caseManagerValues = {
    id: caseManagerId[0]?.id,
    value: caseManagerId[0]?.value,
  };

  const referralType = useWatch({
    control,
    name: 'referralType',
  });
  const referralTypeId = cbpCaseManagerReferenceData?.referralTypes?.filter(
    (item) => (item.value === referralType ? item.id : '')
  );
  const referralTypeValues = {
    athTypId: referralTypeId[0]?.id,
    // athTypId: referralTypeId[0]?.value,
  };

  useEffect(() => {
    dispatch(setCaseManager(caseManagerValues));
    dispatch(setReferralType(referralTypeValues));
  }, [caseManager, referralType]);

  const handleClose = () => {
    setOpen(false);
    reset(defaultValue);
  };

  const onSubmit = () => {
    const url = `${CBP_CASE_MANAGER_UPDATE_URL}/${cbpId}/casemanager`;
    const authorization = authToken;
    const payload = {
      CbpCmID: cbpCaseManagerForm?.CbpCmID,
      caseManagerId: cbpCaseManagerForm?.caseManager?.id,
      referralType: cbpCaseManagerForm?.referralType?.athTypId,
      startDate: cbpCaseManagerForm?.startDate,
      endDate: cbpCaseManagerForm?.endDate,
    };
    if (cbpCaseManagerForm?.newCbpCaseManager === true) {
      const payload = {
        CbpCmID: 0,
        caseManagerId:
          cbpCaseManagerForm?.caseManager?.id === ''
            ? null
            : cbpCaseManagerForm?.caseManager?.id,
        referralType:
          cbpCaseManagerForm?.referralType?.athTypId === ''
            ? null
            : cbpCaseManagerForm?.referralType?.athTypId,
        startDate:
          cbpCaseManagerForm?.startDate === ''
            ? null
            : cbpCaseManagerForm?.startDate,
        endDate:
          cbpCaseManagerForm?.endDate === ''
            ? null
            : cbpCaseManagerForm?.endDate,
      };
      postStopApi({ url, payload, authorization }).then((response) => {
        if (response.succeeded === true) {
          fetchData();
          setFormSuccess(true);
          setIsNewCaseManager(true);
        }
      }).catch((e) => alert(e));
    } else {
      setStopApi({ url, payload, authorization }).then((response) => {
        if (response.succeeded === true) {
          fetchData();
          setFormSuccess(true);
        }
      }).catch((e) => alert(e));
    }
    setOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            maxWidth: 'unset',
            width: '800px',
            maxHeight: 'unset',
          },
        }}
      >
        {openBackdrop ? (
          <Backdrop
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={openBackdrop}
          >
            <CircularProgress />
          </Backdrop>
        ) : (
          <>
            <DialogHeader>
              <DialogHeading variant='h5'>CBP Case Manager</DialogHeading>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </DialogHeader>
            <Grid
              container
              spacing={2}
              style={{ padding: '10px 40px', marginTop: '0px' }}
            >
              <Grid item xs={12} sm={6}>
                <FormInputDropdown
                  name='caseManager'
                  control={control}
                  options={cbpCaseManagerReferenceData?.caseManagers}
                  label='Case Manager'
                  errors={!!errors.caseManager}
                  helperText={errors?.caseManager?.message}
                />
                <FormInputDropdown
                  name='referralType'
                  control={control}
                  options={cbpCaseManagerReferenceData?.referralTypes}
                  label='Referral Type'
                  errors={!!errors.referralType}
                  helperText={errors?.referralType?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormInputText
                  name='startDate'
                  type='date'
                  control={control}
                  shrink={true}
                  label='Start Date'
                  errors={!!errors.startDate}
                  helperText={errors?.startDate?.message}
                  register={register('startDate', {
                    onChange: (e) => dispatch(setStartDate(e.target.value)),
                  })}
                />
                <FormInputText
                  name='endDate'
                  type='date'
                  control={control}
                  shrink={true}
                  label='End Date'
                  errors={!!errors.endDate}
                  helperText={errors?.endDate?.message}
                  register={register('endDate', {
                    onChange: (e) => dispatch(setEndDate(e.target.value)),
                  })}
                />
              </Grid>
              <ButtonWrapper>
                <CancelButton variant='contained' onClick={handleClose}>
                  Cancel
                </CancelButton>
                <SaveButton
                  variant='contained'
                  onClick={handleSubmit(onSubmit)}
                >
                  Save
                </SaveButton>
              </ButtonWrapper>
            </Grid>
          </>
        )}
      </Dialog>
      <FormSuccess
        formSuccess={formSuccess}
        setFormSuccess={setFormSuccess}
        isNewForm={isNewCaseManger}
      />
    </>
  );
};

export default CBPCaseManagerDialog;
